import React, { useEffect, useState } from "react";
import { useAuth } from "../../../AuthProvider";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { getBillingData } from "../../../api/apiClient";

export const Plans = () => {
  // eslint-disable-next-line no-unused-vars
  const [paymentStatus, setPaymentStatus] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState("annual");
  const [currentPlan, setCurrentPlan] = useState(null);

  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = async () => {
    if (currentUser) {
      navigate("/ai-model");
    } else {
      navigate("/signup");
    }
  };

  // Pricing plans data - monthly
  const monthlyPlans = [
    {
      id: "free",
      name: "Free",
      description: "Best for testing Gomens on your first products",
      price: "0",
      discount: "0",
      originalPrice: "0",
      interval: "month",
      trialText: "Generate 10 free images",
      features: [
        { name: "Try Gomens with your first garment images", included: true },
        { name: "Access a limited selection of models", included: true },
        { name: "Use standard model poses", included: true },
        { name: "Generate standard-quality images", included: true },
        { name: "Email support (48h response)", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing", included: false },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: false
    },
    {
      id: "starter",
      name: "Starter",
      description: "For small fashion brands creating visuals for new drops and product pages",
      price: "39",
      discount: "0",
      originalPrice: "39",
      interval: "month",
      trialText: "Generate 50 images per month",
      features: [
        { name: "Create on-model visuals for new products", included: true },
        { name: "More variety across model looks and poses", included: true },
        { name: "More flexibility for catalog and marketing imagery", included: true },
        { name: "High-resolution image exports", included: true },
        { name: "Standard support", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing", included: false },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: false,
      priceId: "price_1SGCS7EFleSS6dXqd3WRYqat",
      link: "https://buy.stripe.com/6oU4gzghXa9r4l0bCK0x205"
    },
    {
      id: "pro",
      name: "Pro",
      description: "For active apparel brands producing visuals across more products each month",
      price: "79",
      discount: "0",
      originalPrice: "79",
      interval: "month",
      trialText: "Generate 200 images per month",
      features: [
        { name: "Create more on-model visuals each month", included: true },
        { name: "Broader model and pose variety", included: true },
        { name: "More flexibility across product and campaign imagery", included: true },
        { name: "High-resolution outputs", included: true },
        { name: "Standard support", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing", included: false },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: true,
      priceId: "price_1SGCRXEFleSS6dXqbcIPPeyj",
      link: "https://buy.stripe.com/00w14n9Tz4P704K8qy0x203"
    },
    {
      id: "growth",
      name: "Growth",
      description: "For growing brands with larger catalogs and higher output needs",
      price: "169",  
      discount: "0",
      originalPrice: "169",
      interval: "month",
      trialText: "Generate 500 images per month",
      features: [
        { name: "Higher monthly image volume", included: true },
        { name: "Broader model and pose variety", included: true },
        { name: "More flexibility across product lines", included: true },
        { name: "High-resolution outputs", included: true },
        { name: "Dedicated support", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing included", included: true },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: false,
      priceId: "price_1SGCRAEFleSS6dXqq9Gk6QGH",
      link: "https://buy.stripe.com/6oUdR93vb4P76t87mu0x201"
    },
  ];

  // Pricing plans data - annual (with discounts applied)
  const annualPlans = [
    {
      id: "free",
      name: "Free",
      description: "Best for testing Gomens on your first products",
      price: "0",
      discount: "0%",
      originalPrice: "0",
      interval: "month",
      trialText: "Generate 10 free images",
      features: [
        { name: "Try Gomens with your first garment images", included: true },
        { name: "Access a limited selection of models", included: true },
        { name: "Use standard model poses", included: true },
        { name: "Generate standard-quality images", included: true },
        { name: "Email support (48h response)", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing", included: false },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: false,
    },
    {
      id: "starter",
      name: "Starter",
      description: "For small fashion brands creating visuals for new drops and product pages",
      price: "397.80",
      discount: "15%",
      originalPrice: "468",
      interval: "year",
      trialText: "Generate 600 images per year",
      features: [
        { name: "Create on-model visuals for new products", included: true },
        { name: "More variety across model looks and poses", included: true },
        { name: "More flexibility for catalog and marketing imagery", included: true },
        { name: "High-resolution image exports", included: true },
        { name: "Standard support", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing", included: false },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: false,
      priceId: "price_1SGCRvEFleSS6dXqzpZE1Rii",
      link: "https://buy.stripe.com/aFa14n0iZepH3gWbCK0x204"
    },
    {
      id: "pro",
      name: "Pro",
      description: "For active apparel brands producing visuals across more products each month",
      price: "758.40",
      discount: "20%",
      originalPrice: "948",
      interval: "year",
      trialText: "Generate 2400 images per year",
      features: [
        { name: "Create more on-model visuals each month", included: true },
        { name: "Broader model and pose variety", included: true },
        { name: "More flexibility across product and campaign imagery", included: true },
        { name: "High-resolution outputs", included: true },
        { name: "Standard support", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing", included: false },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: true,
      priceId: "price_1SGCRLEFleSS6dXqKRK0EflD",
      link: "https://buy.stripe.com/aFa9ATd5L3L3cRw0Y60x202"
    },
    {
      id: "growth",
      name: "Growth",
      description: "For growing brands with larger catalogs and higher output needs",
      price: "1521",
      discount: "25%",
      originalPrice: "2028",
      interval: "year",
      trialText: "Generate 6000 images per year",
      features: [
        { name: "Higher monthly image volume", included: true },
        { name: "Broader model and pose variety", included: true },
        { name: "More flexibility across product lines", included: true },
        { name: "High-resolution outputs", included: true },
        { name: "Dedicated support", included: true },
        { name: "Commercial usage included", included: true },
        { name: "Batch processing included", included: true },
      ],
      buttonColor: "bg-black hover:bg-gray-800",
      popular: false,
      priceId: "price_1SGCR3EFleSS6dXqD5H4VoW8",
      link: "https://buy.stripe.com/3cIcN58Pv3L3dVAfT00x200"
    }    
  ];

  const pricingPlans = (billingCycle === "monthly" ? monthlyPlans : annualPlans).filter(plan => {
    // If user has a paid plan (not free), hide the free plan
    if (currentUser && currentPlan && currentPlan.plan !== "free") {
      return plan.id !== "free";
    }
    // Otherwise show all plans
    return true;
  });

  const handlePayment = (link) => {
    if (!currentUser) {
      // Store the priceId in sessionStorage to use after signup
      sessionStorage.setItem("intendedypaymentlink", link);
      navigate("/signup");
      return;
    }

    window.location.href = link;
  };

  // Fetch current plan information when user is active
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (currentUser) {
        try {
          const authToken = await currentUser.getIdToken();
          const response = await getBillingData({
            Authorization: `Bearer ${authToken}`,
            "X-User-Id": currentUser.uid,
          });
          const billingData = response.data.data;
          if (billingData.current_plan) {
            setCurrentPlan(billingData.current_plan);
          }
        } catch (error) {
          console.error("Error fetching billing data:", error);
        }
      }
    };

    fetchCurrentPlan();
  }, [currentUser]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="mb-4">
            <span className="text-sm sm:text-[16px] font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              Pricing
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Simple pricing for fashion brands
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>creating on-model imagery
          </h1>

          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
            Start free, test the output, and upgrade when you’re ready to create more on-model product visuals without the cost and delay of a traditional photoshoot.
          </p>

          {/* Payment Status or Processing States */}
          {processingPayment && !paymentStatus && !error && (
            <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md mx-auto">
              <h3 className="text-lg font-medium text-blue-800">
                Processing Payment
              </h3>
              <p className="text-sm text-blue-700">
                Please wait while we verify your payment...
              </p>
            </div>
          )}

          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg max-w-md mx-auto">
              <h3 className="text-lg font-medium text-red-800">
                Payment Error
              </h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {paymentStatus && (
            <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg max-w-md mx-auto">
              <h3 className="text-lg font-medium text-green-800">
                Payment Successful!
              </h3>
              <p className="text-sm text-green-700">
                Your {paymentStatus.plan} plan is active until{" "}
                {new Date(paymentStatus.plan_end).toLocaleDateString()}
              </p>
              <p className="text-xs text-green-600">
                Transaction ID: {paymentStatus.transaction_id}
              </p>
            </div>
          )}

          {/* Billing cycle toggle */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center bg-[#F8FAFC] rounded-full p-1 shadow-sm border border-gray-200 w-full max-w-xs sm:max-w-sm">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`cursor-pointer px-4 py-2 sm:px-6 rounded-full text-sm font-medium transition-all duration-200 flex-1 ${
                  billingCycle === "monthly"
                    ? "bg-white text-black shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={`cursor-pointer px-4 py-2 sm:px-6 rounded-full text-sm font-medium transition-all duration-200 flex-1 ${
                  billingCycle === "annual"
                    ? "bg-white text-black shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <span className="hidden sm:inline">Yearly (save up to 25%)</span>
                <span className="sm:hidden">Yearly</span>
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Plans Section */}
        <div className={`max-w-7xl mx-auto ${
          pricingPlans.length === 3
            ? 'flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-6'
            : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6'
        }`}>
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-sm border overflow-hidden ${
                plan.popular
                  ? "border-blue-200 ring-2 ring-blue-500 ring-opacity-50"
                  : "border-gray-200"
              } p-4 sm:p-6 flex flex-col ${
                pricingPlans.length === 3 ? 'w-full sm:w-80 lg:w-80' : ''
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute top-0 -right-2 ">
                  <span className="bg-gradient-to-r from-[#5937F8] to-[#4774FF] text-white text-[14px] px-[16px] py-2 flex items-center justify-center rounded-b-2xl shadow-sm">
                    Most popular
                  </span>
                </div>
              )}

              {/* Plan icon */}
              <div className="mb-6">
                {index === 0 && (
                  <div className="w-8 h-8 text-blue-500">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                )}
                {index === 1 && (
                  <div className="w-8 h-8 text-blue-500">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </div>
                )}
                {index === 2 && (
                  <div className="w-8 h-8 text-blue-500">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5 16L3 5l5.5 4L12 3l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
                    </svg>
                  </div>
                )}
                {index === 3 && (
                  <div className="w-8 h-8 text-blue-500">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Plan name and description */}
              <div className="mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {plan.name}<span className="text-red-700 text-sm sm:text-base">{plan.discount !== "0" && plan.discount !== "0%"? ` (${plan.discount} Off)` : ""}</span>
                </h3>
                <p className="text-gray-600 text-sm sm:text-sm mb-3">{plan.description}</p>
                <p className="text-base font-bold text-gray-900">
                  {plan.trialText}
                </p>
              </div>

              {/* Price */}
              <div className="mb-4 sm:mb-4">
                <div className="flex items-baseline mb-2">
                  {billingCycle === "annual" && plan.discount !== "0%" && plan.discount !== "0" ? (
                    <>
                      <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                        ${(parseFloat(plan.price) / 12).toFixed(2)}
                      </span>
                      <span className="ml-2 text-base sm:text-lg text-gray-500 line-through">
                        ${(parseFloat(plan.originalPrice) / 12).toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                      ${plan.interval=="year"? (parseFloat(plan.price)/12).toFixed(2) : plan.price}
                    </span>
                  )}
                </div>
                {plan.price !== "0" && (
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">
                    USD/month • Billed {plan.interval}ly
                  </div>
                )}
              </div>

              {/* CTA Button */}
              <div className="mb-8">
                {plan.price === "0" && (
                  <p className="text-sm text-center text-gray-500 mb-0">
                    No credit card required
                  </p>
                )}
                {
                currentUser && (
                  (plan.id === "free" && (!currentPlan || currentPlan.plan === "free")) ||
                  (currentPlan && currentPlan.plan === plan.id && currentPlan.period === billingCycle)
                ) ? (
                  <button
                    disabled
                    className="w-full px-6 py-3 rounded-lg font-medium bg-green-600 text-white cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (

                  // <a
                  //  className="btn btn-primary btn-block"
                  //   href={plan.link+"prefilled_email="+(currentUser?.email || "")}
                  //   target="_blank"
                  //   //rel="noopener noreferrer"
                  //   //style={{ textDecoration: 'none' }}
                  //   >
                  <button
                    onClick={() =>
                      plan.price === "0"
                        ? handleGetStarted()
                        : handlePayment(plan.link+"?prefilled_email="+(currentUser?.email || "")+"&client_reference_id="+(currentUser?.uid || "") )
                    }
                    disabled={processingPayment}
                    className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                      processingPayment
                        ? "bg-gray-300 cursor-not-allowed text-gray-500"
                        : "cursor-pointer "+plan.buttonColor + " text-white"
                    }`}
                  >
                    {processingPayment
                      ? "Processing..."
                      : plan.price === "0"
                      ? "Start free"
                      : "Get started"}
                  </button>
                )}
                {plan.price !== "0" && (
                  <p className="text-xs text-center text-gray-500 mt-1">
                    {/*7-day money-back guarantee*/}
                  </p>
                )}
              </div>

              {/* Features */}
              <div className="flex-grow">
                <h4 className="font-medium text-gray-900 mb-4">
                  What's included:
                </h4>
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start text-sm">
                      <div className="flex-shrink-0 mt-0.5 mr-3">
                        {feature.included ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 text-gray-300" />
                        )}
                      </div>
                      <p
                        className={
                          feature.included ? "text-gray-700" : "text-gray-400"
                        }
                      >
                        {feature.name}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
